import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Radio } from "lucide-react";
import { api } from "@/lib/api";
import type { Club, LiveMatch } from "@/types";
import { useAuthStore } from "@/store/auth";
import { Card } from "@/components/ui/Card";
import { Breadcrumbs } from "@/components/ui/Breadcrumbs";
import { LiveMatchVideoPlayer } from "@/components/LiveMatchVideoPlayer";
import { ScoreboardOverlay } from "@/components/ScoreboardOverlay";
import { RefereeCodeSection } from "@/components/RefereeCodeSection";

const timeFormatter = new Intl.DateTimeFormat("es-ES", { hour: "2-digit", minute: "2-digit" });

function MatchCard({ match }: { match: LiveMatch }) {
  const camera = match.cameras[0];

  return (
    <Card className="overflow-hidden p-0">
      <div className="aspect-video w-full bg-ink-950">
        {camera ? (
          <LiveMatchVideoPlayer
            offerUrl={`/matches/live/camera/${camera.id}/webrtc-offer`}
            terminateUrl="/matches/live/webrtc-session"
            className="h-full w-full"
            overlay={match.court && <ScoreboardOverlay courtId={match.court.id} initial={match.scoreboard} />}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-sm text-ink-300">
            Sin cámara configurada en esta pista
          </div>
        )}
      </div>
      <div className="flex flex-col gap-3 p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-semibold text-ink-900">{match.court?.name ?? match.courtLabel}</p>
            <p className="text-sm text-ink-400">
              {match.club.nombre} · {match.customerName}
            </p>
          </div>
          <div className="text-right text-xs text-ink-400">
            <span className="inline-flex items-center gap-1 font-medium text-red-600">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-600" />
              EN DIRECTO
            </span>
            <p className="mt-1">{timeFormatter.format(new Date(match.startsAt))}</p>
          </div>
        </div>
        <RefereeCodeSection reservationId={match.id} refereeCode={match.refereeCode} queryKey={["matches", "live"]} />
      </div>
    </Card>
  );
}

export function LiveMatchesPage() {
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const ownedClubs = user?.ownedClubs ?? [];
  const [clubId, setClubId] = useState("");

  const { data: clubs } = useQuery({
    queryKey: ["clubs"],
    queryFn: async () => (await api.get<{ data: Club[] }>("/clubs?limit=100")).data.data,
    enabled: isSuperAdmin,
  });
  const clubFilterOptions = isSuperAdmin ? (clubs ?? []) : ownedClubs.length > 1 ? ownedClubs : [];

  const { data: matches, isLoading } = useQuery({
    queryKey: ["matches", "live", clubId],
    queryFn: async () => (await api.get<LiveMatch[]>(`/matches/live${clubId ? `?clubId=${clubId}` : ""}`)).data,
    refetchInterval: 30_000,
  });

  return (
    <div className="flex flex-col gap-8">
      <Breadcrumbs items={[{ label: "Partidos" }, { label: "En directo" }]} />

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50 text-red-600">
            <Radio className="h-6 w-6" />
          </span>
          <div>
            <h1 className="text-2xl font-bold text-ink-900">Partidos en directo</h1>
            <p className="text-ink-400">Pistas con un partido en curso ahora mismo, con vídeo en vivo si hay cámara.</p>
          </div>
        </div>
        {clubFilterOptions.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink-600">Club</label>
            <select
              value={clubId}
              onChange={(e) => setClubId(e.target.value)}
              className="rounded-xl border border-ink-100 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
            >
              <option value="">Todos</option>
              {clubFilterOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {isLoading ? (
        <p className="text-sm text-ink-400">Cargando…</p>
      ) : !matches?.length ? (
        <Card className="border-dashed bg-white/60">
          <p className="text-sm text-ink-400">Ahora mismo no hay ningún partido en curso.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          {matches.map((match) => (
            <MatchCard key={match.id} match={match} />
          ))}
        </div>
      )}
    </div>
  );
}
